import { createShapeId } from 'tldraw';

import { getActivityId, getPinCardSize, normalizePlacements } from './autoLayout';
import { pinCardSyncingIds } from './pinCardDrag';
import { normalizeLogicalFrameId } from './frameMembership';

const SHAPE_PREFIX = 'pin:';

function activityById(activities, id) {
  return activities.find(a => getActivityId(a) === String(id));
}

function getPlacementSize(placement, activity, getPinCardSize) {
  return getPinCardSize(activity, placement.width, {
    manualResize: Boolean(placement.manualSize),
  });
}

function placementsToShapes(activities, placements, resolvePinCardSize) {
  return placements
    .map(placement => {
      const activityId = String(placement.activityId);
      const activity = activityById(activities, activityId);
      if (!activity) return null;

      const { width, height } = getPlacementSize(placement, activity, resolvePinCardSize);
      const frameId =
        placement.frameId == null ? null : normalizeLogicalFrameId(placement.frameId);

      return {
        id: createShapeId(`${SHAPE_PREFIX}${activityId}`),
        type: 'pin-card',
        x: placement.x,
        y: placement.y,
        meta: {
          activityId,
          frameId,
        },
        props: {
          w: width,
          h: height,
        },
      };
    })
    .filter(Boolean);
}

function pinShapeNeedsReplace(existing, desired) {
  return (
    existing.x !== desired.x ||
    existing.y !== desired.y ||
    existing.props?.w !== desired.props.w ||
    Math.abs((existing.props?.h ?? 0) - (desired.props?.h ?? 0)) > 2 ||
    existing.meta?.activityId !== desired.meta.activityId ||
    (existing.meta?.frameId || null) !== (desired.meta?.frameId || null)
  );
}

/**
 * Keep category frames behind pin cards so opaque frame fills do not cover pins.
 * Order (back → front): frames, pin cards, suggest pins, user drawings.
 * Pins stay page-level (not frame children) so frames never clip them.
 */
export function layerBoardContent(editor) {
  if (!editor) return;

  const pageId = editor.getCurrentPageId();
  const shapes = editor.getCurrentPageShapes();
  const frameIds = shapes
    .filter(shape => shape.type === 'frame' && shape.parentId === pageId)
    .map(shape => shape.id);
  const pinIds = shapes
    .filter(shape => shape.type === 'pin-card' && shape.parentId === pageId)
    .map(shape => shape.id);
  const suggestPinIds = shapes
    .filter(shape => shape.type === 'suggest-pin' && shape.parentId === pageId)
    .map(shape => shape.id);
  const conversationPinIds = shapes
    .filter(shape => shape.type === 'conversation-pin' && shape.parentId === pageId)
    .map(shape => shape.id);
  const toBack = [...frameIds, ...pinIds, ...suggestPinIds, ...conversationPinIds];

  if (toBack.length) editor.sendToBack(toBack);
}

/**
 * Sync pin cards without wiping the rest of the tldraw document (drawings, text, etc.).
 * Prefer update/create/delete deltas over delete-all + recreate.
 */
export function syncPinShapes(editor, activities, board, resolvePinCardSize = getPinCardSize) {
  if (!board?.placements?.length) return;

  const placements = normalizePlacements(activities, board.placements);
  const desiredShapes = placementsToShapes(activities, placements, resolvePinCardSize);
  // Only pin-cards — AI text/arrow annotations also set meta.activityId and must not
  // receive pin props (e.g. `h`), which invalidates tldraw text shapes.
  const existing = editor
    .getCurrentPageShapes()
    .filter(shape => shape.type === 'pin-card' && shape.meta?.activityId);
  const existingByActivityId = new Map(
    existing.map(shape => [String(shape.meta.activityId), shape])
  );
  const desiredActivityIds = new Set();
  const pageId = editor.getCurrentPageId();

  const toCreate = [];
  const toUpdate = [];

  for (const desired of desiredShapes) {
    const activityId = String(desired.meta.activityId);
    desiredActivityIds.add(activityId);
    const current = existingByActivityId.get(activityId);

    if (!current) {
      toCreate.push({ ...desired, parentId: pageId });
      continue;
    }

    // Legacy boards may have pins parented into frames; keep page-level to avoid clipping.
    if (current.parentId !== pageId) {
      try {
        editor.reparentShapes([current.id], pageId);
      } catch {
        /* optional */
      }
    }

    const latest = editor.getShape(current.id) || current;
    if (pinShapeNeedsReplace(latest, desired)) {
      toUpdate.push({
        id: current.id,
        type: 'pin-card',
        x: desired.x,
        y: desired.y,
        meta: {
          ...latest.meta,
          activityId,
          frameId: desired.meta?.frameId ?? null,
        },
        props: {
          w: desired.props.w,
          h: desired.props.h,
        },
      });
    }
  }

  const toRemove = existing
    .filter(shape => !desiredActivityIds.has(String(shape.meta.activityId)))
    .map(shape => shape.id);

  if (toRemove.length) editor.deleteShapes(toRemove);
  if (toCreate.length) editor.createShapes(toCreate);
  if (toUpdate.length) {
    for (const shape of toUpdate) pinCardSyncingIds.add(shape.id);
    try {
      editor.updateShapes(toUpdate);
    } finally {
      for (const shape of toUpdate) pinCardSyncingIds.delete(shape.id);
    }
  }

  layerBoardContent(editor);
}
